import './Summary.scss';

import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { defaultTimelineFilterParams } from './TimelineView';
import { getTimelineSummarySelector } from './@arch/selectors';
import { SonarAlert } from '@ui/SonarAlert';
import { SonarCard } from '@ui/general/SonarCard';
import { TimelineChangeType } from '@sonar-software/sonar-core';
import { TimelineDatePicker } from './TimelineDatePicker';
import { TimelineSynopsis } from './TimelineSynopsis';
import { UserChangesChart } from './UserChangesChart';

import { GroupedChangesChart, GroupedChangesChartParams } from './GroupedChangesChart';
import React, { useCallback, useMemo, useState } from 'react';
import { TimelineFilters, TimelineFiltersParams } from './filters/TimelineFilters';
import { UpdateQueryParams, useSonarSelector } from '@hooks';

export type TimelineSummaryParams = TimelineFiltersParams & GroupedChangesChartParams;

interface TimelineSummaryProps {
	onChange: UpdateQueryParams<TimelineSummaryParams>;
	params: TimelineSummaryParams;
}

export const TimelineSummary: React.FC<TimelineSummaryProps> = ({ onChange, params }) => {
	const data = useSonarSelector(getTimelineSummarySelector.data);
	const [ highlightedChangeType, setHighlightedChangeType ] = useState<TimelineChangeType>();
	const [ selectedChangeType, setSelectedChangeType ] = useState<TimelineChangeType>();

	const clearSelectedChangeType = useCallback(() => {
		setSelectedChangeType(undefined);
	}, []);

	const clearFilters = useCallback(() => {
		onChange({
			...params,
			...defaultTimelineFilterParams
		});
	}, [ onChange, params ]);

	const hasFiltersApplied = useMemo(() => {
		const { applicationIds,
			tagIds,
			objectFilter,
			modifiedByUsernames,
			system,
			objectNameFilter,
			isFollowing
		 } = params;

		return Boolean(
			objectNameFilter ||
			!system ||
			modifiedByUsernames?.length ||
			Object.keys(objectFilter ?? {}).length ||
			isFollowing !== undefined ||
			applicationIds?.length ||
			tagIds?.length
		);
	}, [ params ]);

	const extra =
		<TimelineDatePicker
			endDate={ params.end }
			onChange={ onChange }
			startDate={ params.start } />;

	return (
		<SonarCard
			extra={ data ? extra : null }
			id='timeline-summary'
			loading={ !data }
			title={ COPY_SPECIFIC.TIMELINE_SUMMARY }
			variant='borderless'>
			<SonarAlert
				active={ hasFiltersApplied }
				afterClose={ clearFilters }
				closable
				closeText={ COPY_SPECIFIC.CLEAR_FILTERS }
				message={ COPY_SPECIFIC.FILTERS_APPLIED }
				showIcon
				type='info' />
			<div className='summary-sections'>
				<TimelineFilters
					clearSelectedChangeType={ clearSelectedChangeType }
					highlightedChangeType={ highlightedChangeType }
					onChange={ onChange }
					params={ params }
					selectedChangeType={ selectedChangeType } />
				<div className='charts-and-stats'>
					<div className='top-section'>
						<TimelineSynopsis
							hideSystem={ !params?.system }
							onChange={ onChange }
							onChangeTypeClicked={ setSelectedChangeType }
							onChangeTypeHovered={ setHighlightedChangeType } />
						<UserChangesChart
							modifiedBy={ params?.modifiedByUsernames }
							onChange={ onChange } />
					</div>
					<GroupedChangesChart
						grouping={ params.timelineGroupingType }
						onChange={ onChange } />
				</div>
			</div>
		</SonarCard>
	);
};
